import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Package, AlertTriangle, CheckCircle, Search, Edit, Save, X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNumber = (num) => {
  return num?.toLocaleString() || '0';
};

const InventoryPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [stockForm, setStockForm] = useState({
    new_stock: '',
    reason: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      setProducts([...response.data].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStock = (product) => {
    setEditingProduct(product);
    setStockForm({
      new_stock: product.current_stock.toString(),
      reason: ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveStock = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/products/${editingProduct.id}/stock`, {
        new_stock: parseFloat(stockForm.new_stock),
        reason: stockForm.reason || undefined
      });
      toast.success(`Stock updated for ${editingProduct.name}`);
      setEditDialogOpen(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stock');
    }
  };

  const totalValue = products.reduce((sum, p) => sum + (p.current_stock * p.base_price), 0);
  const lowStockProducts = products.filter(p => p.current_stock <= p.reorder_level);
  const inStockProducts = products.filter(p => p.current_stock > p.reorder_level);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' ||
                         (filter === 'low' && product.current_stock <= product.reorder_level) ||
                         (filter === 'in' && product.current_stock > product.reorder_level);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Inventory</h1>
          <p className="text-muted-foreground">Track and manage your stock levels</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Products</p>
              <p className="text-3xl font-heading font-bold text-foreground">{products.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Low Stock Items</p>
              <p className="text-3xl font-heading font-bold text-red-600">{lowStockProducts.length}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">In Stock</p>
              <p className="text-3xl font-heading font-bold text-green-600">{inStockProducts.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Inventory Value</p>
              <p className="text-2xl font-heading font-bold text-foreground">RWF {formatNumber(totalValue)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl border border-border shadow-sm">
        <div className="flex items-center space-x-2 mb-4">
          <Search className="w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search inventory by product name or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
            data-testid="inventory-search-input"
          />
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid="filter-all-btn"
          >
            All Products ({products.length})
          </button>
          <button
            onClick={() => setFilter('in')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'in' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid="filter-in-stock-btn"
          >
            In Stock ({inStockProducts.length})
          </button>
          <button
            onClick={() => setFilter('low')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'low' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid="filter-low-stock-btn"
          >
            Low Stock ({lowStockProducts.length})
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="inventory-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Product</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Category</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Current Stock</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Reorder Level</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Unit Price</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Stock Value</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Status</th>
                <th className="px-6 py-4 text-center text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">
                    No products match this filter.
                  </td>
                </tr>
              )}
              {filteredProducts.map((product) => {
                const isLowStock = product.current_stock <= product.reorder_level;
                const stockValue = product.current_stock * product.base_price;
                return (
                  <tr key={product.id} className="hover:bg-muted/30 transition-colors" data-testid="inventory-row">
                    <td className="px-6 py-4 text-sm font-medium text-foreground">{product.name}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{product.category}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={isLowStock ? 'text-red-600 font-semibold' : 'text-foreground'}>
                        {product.current_stock} {product.unit}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{product.reorder_level} {product.unit}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">RWF {formatNumber(product.base_price)}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-foreground">RWF {formatNumber(stockValue)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isLowStock ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {isLowStock ? 'Low Stock' : 'In Stock'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditStock(product)}
                        data-testid={`edit-stock-${product.id}`}
                        title="Edit Stock"
                      >
                        <Edit className="w-4 h-4 text-blue-600" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Stock Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) {
          setEditingProduct(null);
          setStockForm({ new_stock: '', reason: '' });
        }
      }}>
        <DialogContent data-testid="edit-stock-dialog">
          <DialogHeader>
            <DialogTitle>Edit Stock: {editingProduct?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveStock} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Current Stock</label>
              <p className="text-lg font-semibold text-muted-foreground">
                {editingProduct?.current_stock} {editingProduct?.unit}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">New Stock Level</label>
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={stockForm.new_stock}
                  onChange={(e) => setStockForm({ ...stockForm, new_stock: e.target.value })}
                  placeholder="Enter new stock level"
                  data-testid="new-stock-input"
                />
                <span className="text-muted-foreground">{editingProduct?.unit}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Reason (Optional)</label>
              <Input
                type="text"
                value={stockForm.reason}
                onChange={(e) => setStockForm({ ...stockForm, reason: e.target.value })}
                placeholder="e.g., Physical count adjustment, damaged goods..."
                data-testid="stock-reason-input"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button type="submit" data-testid="save-stock-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Stock
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
